import { createFileRoute, Link } from "@tanstack/react-router";
import { Sun, Waves, Coffee, Bike, Phone } from "lucide-react";
import { canonical } from "@/lib/seo";

export const Route = createFileRoute("/summer-stays-crystal-beach")({
  head: () => ({
    meta: [
      { title: "Summer Stays at Crystal Beach - Malak BnB Resort, Lake Erie" },
      {
        name: "description",
        content:
          "Spend your summer at Malak BnB Resort in Crystal Beach, Ontario. Steps from Bay Beach on Lake Erie, with an outdoor heated pool, breakfast, and direct reservations.",
      },
      {
        property: "og:title",
        content: "Summer Stays at Crystal Beach - Malak BnB Resort, Lake Erie",
      },
      {
        property: "og:description",
        content:
          "Crystal Beach in summer: Bay Beach, Lake Erie, an outdoor pool, and a family-owned BnB worth coming back to.",
      },
      { property: "og:url", content: "https://malakbnbresort.ca/summer-stays-crystal-beach" },
      { property: "og:image", content: "/images/beach-bay.webp" },
    ],
    links: [canonical("/summer-stays-crystal-beach")],
  }),
  component: SummerStaysPage,
});

const SUMMER_HIGHLIGHTS = [
  {
    icon: Waves,
    title: "Bay Beach, Five Minutes on Foot",
    desc: "Crystal Beach's main beach sits a short walk from the property. Fine sand, calm water in Lake Erie, and long summer afternoons that somehow always last exactly as long as you need.",
  },
  {
    icon: Sun,
    title: "The Outdoor Heated Pool",
    desc: "For those afternoons when Bay Beach is full, or when you just want to stay close to home. The outdoor pool at Malak is heated, the lounge chairs are there, and the pace is unhurried.",
  },
  {
    icon: Coffee,
    title: "Breakfast Every Morning",
    desc: "Continental breakfast is included with every room. Summer mornings at Malak have a particular quality to them: a proper meal, the smell of coffee, and no reason to rush anywhere.",
  },
  {
    icon: Bike,
    title: "Cycling and Town Streets",
    desc: "Crystal Beach has the right energy for a cycling weekend. Flat roads, a charming town centre, local cafes, and lake views on most routes. Rent a bike and explore at your own pace.",
  },
];

function SummerStaysPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[400px] overflow-hidden">
        <img
          src="/images/beach-bay.webp"
          alt="Bay Beach at Crystal Beach Ontario in summer on Lake Erie"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/65 via-foreground/10 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-16 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <p className="text-white/70 font-semibold text-sm uppercase tracking-widest mb-3">
              Lake Erie, Ontario
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading text-white leading-tight max-w-2xl">
              Summer Stays at Crystal Beach
            </h1>
            <p className="mt-4 text-white/80 text-lg max-w-xl leading-relaxed">
              Bay Beach is five minutes from the door. The pool is heated. Breakfast is included.
              This is what a proper summer getaway looks like.
            </p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-4">
            Crystal Beach in Summer
          </p>
          <p className="text-2xl font-heading text-foreground leading-snug mb-8">
            Crystal Beach has a summer that feels like it belongs to another era.
          </p>
          <div className="space-y-5 text-muted-foreground leading-relaxed">
            <p>
              The town quiets down in a way that cities never manage. Bay Beach fills up with
              families and couples who have figured out what locals have known for years: Lake Erie
              gets warm by June and stays that way through August, the water is calm, and the walk
              from town to the sand takes about five minutes.
            </p>
            <p>
              Malak BnB Resort sits right in that pocket. At 264 Ridge Road South, you are close
              enough to Bay Beach to hear it if the wind is right, and close enough to the town
              centre that a walk for coffee or dinner is never an ordeal.
            </p>
            <p>
              The property has an outdoor heated pool for the afternoons when you want to stay
              close. Breakfast is ready each morning. The rooms are air-conditioned and properly
              comfortable for summer heat. And because Malak takes direct reservation requests, you
              are dealing with the family who runs the place, not a booking platform.
            </p>
            <p>
              Summer weekends fill up. If you are looking at July or August dates, especially around
              Canada Day or Civic Holiday long weekends, it is worth submitting your request early.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/book"
              className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Request Your Summer Stay
            </Link>
            <Link
              to="/rooms"
              className="inline-flex items-center justify-center rounded-full border border-border px-8 py-3 text-sm font-semibold text-foreground hover:bg-muted transition-colors"
            >
              View Rooms and Rates
            </Link>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
              What Summer Looks Like Here
            </p>
            <h2 className="text-3xl font-heading text-foreground">
              Four Things That Make a Crystal Beach Summer
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {SUMMER_HIGHLIGHTS.map((item) => (
              <div
                key={item.title}
                className="bg-background rounded-2xl p-8 border border-border/50 flex gap-5"
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <item.icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading text-lg text-foreground mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pool photo */}
      <section className="relative h-[420px] sm:h-[500px] overflow-hidden">
        <img
          src="/images/gallery-pool-outdoor.webp"
          alt="Outdoor heated pool at Malak BnB Resort Crystal Beach in summer"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          <p className="text-white/70 text-sm font-semibold uppercase tracking-widest mb-4">
            Malak BnB Resort
          </p>
          <h2 className="text-3xl sm:text-4xl font-heading text-white max-w-xl leading-tight">
            Warm waters and nothing on the schedule.
          </h2>
          <Link
            to="/book"
            className="mt-8 inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Request to Book Your Summer Stay
          </Link>
        </div>
      </section>

      {/* Pexels beach photo + copy */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="/images/pexels-sunny-beach.webp"
                alt="Sunny beach day with clear blue skies on a summer getaway"
                className="w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
              <p className="text-xs text-muted-foreground px-4 py-2 bg-card">
                Photo by Anton Massalov via{" "}
                <a
                  href="https://www.pexels.com/photo/sunny-beach-day-with-clear-blue-skies-33157635/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-foreground"
                >
                  Pexels
                </a>
              </p>
            </div>
            <div>
              <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-4">
                Beyond the Beach
              </p>
              <h2 className="text-3xl font-heading text-foreground leading-snug mb-6">
                Crystal Beach is Better With a Base Worth Coming Back To
              </h2>
              <div className="space-y-5 text-muted-foreground leading-relaxed">
                <p>
                  A summer day in Crystal Beach tends to follow its own rhythm. Morning breakfast at
                  the property, a walk down to Bay Beach, maybe a coffee and a browse through the
                  shops in town. An afternoon by the pool if the beach gets crowded. Dinner in the
                  dining room or a short walk to a local restaurant.
                </p>
                <p>
                  If you want to push out further, Niagara Falls is 30 minutes east. Fort Erie is 10
                  minutes south. For those coming from across the border, the Peace Bridge into
                  Buffalo is about 15 minutes. Crystal Beach has always been a crossing-point town,
                  and it still draws a good number of visitors from upstate New York.
                </p>
                <p>
                  The property is at 264 Ridge Road South, and the rooms start from C$345 per night
                  with seasonal variations. Summer is the busiest season, so dates in July and
                  August tend to go first.
                </p>
              </div>
              <div className="mt-8">
                <Link
                  to="/explore"
                  className="inline-flex items-center text-primary font-semibold text-sm hover:underline"
                >
                  Explore Crystal Beach and the area →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Niagara day trip teaser */}
      <section className="py-16 bg-card">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
            While You Are Here
          </p>
          <h2 className="text-3xl font-heading text-foreground mb-5">
            A Short Drive to Niagara Falls
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Niagara Falls is 30 minutes from Crystal Beach. Many guests use Malak as a base for a
            Niagara day trip, avoiding the busy tourist hotels while still making the drive in under
            half an hour. It is one of the better decisions you can make for a summer long weekend
            in Southern Ontario.
          </p>
          <Link
            to="/explore"
            className="mt-6 inline-flex items-center text-primary font-semibold text-sm hover:underline"
          >
            Explore what is nearby →
          </Link>
        </div>
      </section>

      {/* Rooms reminder */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
              Choose Your Room
            </p>
            <h2 className="text-3xl font-heading text-foreground">Four Rooms for a Summer Stay</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              All rooms include air conditioning, flat screen TV, free Wi-Fi, and in-room safe.
              Continental breakfast is included every morning. Rates start from C$345 per night.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              {
                name: "2 Double Beds",
                price: "From C$345",
                img: "/images/room-double-beds.webp",
                alt: "Two double beds room at Malak BnB Resort Crystal Beach",
              },
              {
                name: "Standard Double",
                price: "From C$345",
                img: "/images/room-standard-double.webp",
                alt: "Standard double bed room at Malak BnB Resort",
              },
              {
                name: "Deluxe Queen",
                price: "From C$355",
                img: "/images/room-deluxe-queen.webp",
                alt: "Deluxe queen bed room with fireplace",
              },
              {
                name: "Queen Suite",
                price: "From C$380",
                img: "/images/room-queen-suite.webp",
                alt: "Queen bed suite with living area",
              },
            ].map((room) => (
              <Link
                key={room.name}
                to="/rooms"
                className="group block rounded-2xl overflow-hidden bg-card shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={room.img}
                    alt={room.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-sm text-foreground">{room.name}</h3>
                  <p className="text-xs text-primary font-medium mt-1">{room.price}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/rooms"
              className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              View All Rooms and Rates
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">
            Summer 2026
          </p>
          <h2 className="text-3xl sm:text-4xl font-heading text-primary-foreground leading-tight">
            Summer fills up. Claim your dates now.
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Submit a direct reservation request for your Crystal Beach summer stay. The Malak team
            personally reviews and confirms every booking, with no third-party fees.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/book"
              className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg"
            >
              Request Your Summer Stay
            </Link>
            <a
              href="tel:+12899298055"
              className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors"
            >
              <Phone className="h-4 w-4" />
              1-289-929-8055
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
